import React, { useContext, useMemo } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import CollageButton from './CollageButton';
import './TournamentSummaryCard.css';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import {calculateTournamentRankings} from "../utils/tournamentScoring"

/**
 * TournamentSummaryCard component displays a summary of a player's performance in a tournament
 * 
 * @param {Object} props - Component props
 * @param {Array} props.games - Array of games in the tournament
 * @param {string} props.username - Username of the player
 * @param {number} props.totalPossiblePoints - Total possible points in the tournament (50 per game)
 * @param {string} props.overallRank - Player's overall rank in the tournament
 */
function TournamentSummaryCard({ games, username, totalPossiblePoints = 0, overallRank = '-' }) {
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  const { theme } = useContext(ThemeContext);
  
// TODO use tournament scoring here? probably not, maybe part of context?

  const {
    pointsToNextRank,
    pointsToCutoff,
    totalPoints,
    initialsUsed,
    bestRank,
    worstRank,
    bestGame,
    worstGames,
    unrankedGames,
} = useMemo(() => {
    return  calculateTournamentRankings(games, tournamentAppConfiguration, username, overallRank)
  }, [games, username, tournamentAppConfiguration, overallRank]);
  
  return (
    <div className={`tournament-summary-card ${theme}`}>

      <div className="summary-content">
        {/* Points Summary Section */}
        <div className="summary-section points-section">

          <h3>Points</h3>
          {username ? (
            <div className="points-display">
              <div className="points-value-container">
                <span className="points-value" data-testid="total-points">{totalPoints}</span>
                {tournamentAppConfiguration.showPointPercentage &&
                    <>
                      <span className="points-divider">/</span>
                      <span className="points-max">{totalPossiblePoints}</span>
                    </>
                }
              </div>
              {/* Points to next rank information */}
              <div className="points-to-next-rank">
                {pointsToNextRank !== null && (
                  <div className="next-rank-text">
                    {Math.abs(pointsToNextRank)} more points to gain 1 rank
                  </div>
                )}
                {pointsToCutoff !== null && (
                  <div className="rank50-text">
                    {Math.abs(pointsToCutoff)} more points to reach rank {tournamentAppConfiguration.cutoffRank}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-user-message">
              Search for a username to see points summary
            </div>
          )}
        </div>
        
        {/* Rank Summary Section */}
        <div className="summary-section ranks-section">
          {username ? (
            <div className="ranks-display">
              <div className="rank-item overall">
                <div className="rank-label">Overall Rank</div>
                <div className="rank-value">{overallRank}</div>
              </div>
              <div className="best-worst-container">
                <div className="rank-item best">
                  <div className="rank-label">Best Rank</div>
                  <div className="rank-value">{bestRank === Infinity ? '-' : `#${bestRank}`}</div>
                  <div className="rank-game">{bestGame ? ` (${bestGame})` : ''}</div>
                </div>
                <div className="rank-item worst">
                  <div className="rank-label">Worst Ranks</div>
                  <div className="rank-value">{worstRank === 0 ? '-' : `#${worstRank}`}</div>
                  {worstGames.length > 0 && (
                    <div className="rank-game">
                      ({worstGames.join(', ')})
                    </div>
                  )}
                </div>
              </div>
              {unrankedGames.length > 0 && (
                  <div className="unranked-games">
                    <div className="unranked-label">Unranked Games:</div>
                    <div className="unranked-list">{unrankedGames.join(', ')}</div>
                  </div>
              )}
            </div>
          ) : (
            <div className="no-user-message">
              Search for a username to see rank summary
            </div>
          )}
        </div>
        
        {/* Initials Used Section */}
        {username && initialsUsed.size > 0 && (
          <div className="summary-section initials-section">
            <h3>Initials Used</h3>
            <div className="initials-list">{Array.from(initialsUsed).join(', ')}</div>
          </div>
        )}
        
        {/* Add Collage Button */}
        {tournamentAppConfiguration.showCollage && <CollageButton username={username} overallRank={overallRank} />}
      </div>
    </div>
  );
}

export default TournamentSummaryCard;